import { InputHelperText, useInput, type Validator } from "react-admin";
import { Button, FormControl, FormHelperText, InputLabel, styled, useTheme } from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import { ReactElement } from "react";
import { TinyColor } from "@ctrl/tinycolor";

interface ColorInputProps {
  label?: string | ReactElement | false;
  source: string;
  defaultValue?: string;
  helperText?: string | ReactElement | false;
  validate?: Validator | Validator[];
  size?: "small";
}
export function ColorInput(props: ColorInputProps) {
  const { label, helperText, size, validate, ...rest } = props;
  const {
    field,
    isRequired,
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
  } = useInput({
    validate: [
      (value: undefined | null | string) => {
        if (value) {
          if (new TinyColor(value).isValid) {
            return null;
          } else {
            return "Bitte gib einen gültigen Farbwert ein oder benutze einfach den [Farbe auswählen ...]-Button.";
          }
        }
      },
      ...(Array.isArray(validate) ? validate : validate ? [validate] : []),
    ],
    ...rest,
  });
  return (
    <FormControl>
      {label && (
        <InputLabel required={isRequired} error={invalid}>
          {label}
        </InputLabel>
      )}
      <MuiColorInput
        {...field}
        format="hex"
        adornmentPosition="start"
        Adornment={ColorButton}
        isAlphaHidden
        size={size}
      />
      {(error || helperText !== false) && (
        <FormHelperText error={(isTouched || isSubmitted) && invalid}>
          <InputHelperText
            touched={isTouched || isSubmitted}
            error={error?.message}
            helperText={props.helperText ?? "\u00A0"}
          />
        </FormHelperText>
      )}
    </FormControl>
  );
}

export function ColorButton(props: any) {
  const { bgColor, className, disablePopover, isBgColorValid, ...restButtonProps } = props;
  const theme = useTheme();
  const tinyColor = new TinyColor(bgColor);
  const invalidColorFallback = `linear-gradient(45deg, ${theme.palette.divider} 25%, transparent 25%), linear-gradient(135deg, ${theme.palette.divider} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${theme.palette.divider} 75%), linear-gradient(135deg, transparent 75%, ${theme.palette.divider} 75%) /*! @noflip */`;
  return (
    <_StyledColorButton
      style={{
        backgroundColor: isBgColorValid ? bgColor : undefined,
        backgroundImage: isBgColorValid ? undefined : invalidColorFallback,
        color:
          !isBgColorValid || tinyColor.getBrightness() > 150 ? theme.palette.common.black : theme.palette.common.white,
        cursor: disablePopover ? "default" : undefined,
      }}
      className={`MuiColorInput-Button ${className || ""}`}
      variant="text"
      disableElevation={false}
      {...restButtonProps}
    >
      Farbe auswählen ...
    </_StyledColorButton>
  );
}

const _StyledColorButton = styled(Button)({
  backgroundSize: "8px 8px",
  backgroundPosition: "0 0, 4px 0, 4px -4px, 0px 4px",
  transition: "none",
  boxShadow: "0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)",
  border: 0,
  borderRadius: 4,
  width: "24px",
  aspectRatio: "1 / 1",
  height: "24px",
  minWidth: "150px",
});
